import React, { FC } from "react";
import styled, { css, keyframes } from "styled-components";

import CHAT_SRC from "../../Icons/CosmoMark24.svg";

/**
 * THE CHAT LAUNCHER — one press opens the support assistant, with nothing typed in it.
 *
 * WHERE IT SITS: FLOATING, BOTTOM-RIGHT, AND IT IS DRAWN BY THE PANEL.
 * ---------------------------------------------------------------------------
 * 2026-10-01 design review: in the header's 16px icon box, flattened to one blue by the
 * header's recolour filter, the CoSMo mark could not be told apart from a generic glyph until
 * it was shown larger. The icon was kept and the CONTROL moved: a 56px bubble in the corner
 * the panel opens from, showing the mark at 32px in its own two colours. That is why this is
 * no longer a header control and wears none of the header's classes — the filter those classes
 * carry is what made the mark unreadable.
 *
 * `SupportAssistant` renders it whenever its panel is CLOSED, at the panel's own anchor
 * (`right: 24px; bottom: 72px`), so the bubble and the panel occupy one corner and swap. Being
 * drawn by the panel also replaces the `isSupportAssistantMounted()` gate the header copy
 * needed: a bubble rendered by the panel cannot exist without the panel. The feature flag and
 * the BFF's `503` retirement come with it for the same reason.
 *
 * It steps left of the Submit Feedback drawer and above its full-viewport overlay on the same
 * terms as the panel (`$offset`), or it would sit under the dimmed overlay unclickable.
 *
 * ATTENTION. The halo pulses three times when the bubble first appears and then stops — a
 * standing animation on every screen is noise on a shop-floor board that is left open all
 * shift. Nothing moves under `prefers-reduced-motion`.
 *
 * ⚠️ The icon is still `Icons/CosmoMark24.svg` (masked knock-outs). On this white bubble it
 * renders as authored; never an `e-icons` glyph — they render blank app-wide.
 */

const DRAWER_WIDTH = "420px"; // Keep in step with `ModalBox` in DiagnosticModal.styles.ts.

const halo = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 145, 186, 0.45); }
  70%  { box-shadow: 0 0 0 14px rgba(0, 145, 186, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 145, 186, 0); }
`;

const Bubble = styled.button<{ $offset?: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 72px;
  z-index: 900;

  ${({ $offset }) =>
    $offset &&
    css`
      right: calc(${DRAWER_WIDTH} + 24px);
      z-index: 6100;
    `}

  display: inline-flex;
  align-items: center;
  height: 56px;
  min-width: 56px;
  padding: 0 12px;
  margin: 0;
  border: 1px solid rgba(196, 199, 200, 0.6);
  border-radius: 28px;
  background: #ffffff;
  color: #004d9e;
  box-shadow: 0 6px 18px rgba(54, 66, 74, 0.22);
  font-family: "Noto Sans", sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 24px;
  cursor: pointer;
  transition: box-shadow 140ms ease-out, border-color 140ms ease-out;

  /* The halo is its own layer so it does not fight the bubble's resting shadow. */
  &::after {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    pointer-events: none;
    animation: ${halo} 1.8s ease-out 0.6s 3;
  }

  img {
    width: 32px;
    height: 32px;
    flex: none;
  }

  /* "Ask CoSMo" slides out on hover / keyboard focus. */
  .launcher-label {
    max-width: 0;
    overflow: hidden;
    white-space: nowrap;
    opacity: 0;
    transition: max-width 180ms ease-out, opacity 140ms ease-out, margin 180ms ease-out;
  }

  &:hover,
  &:focus-visible {
    border-color: #0091ba;
    box-shadow: 0 8px 24px rgba(0, 77, 158, 0.28);

    .launcher-label {
      max-width: 120px;
      margin: 0 6px 0 8px;
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: 2px solid #0091ba;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      animation: none;
    }

    .launcher-label {
      transition: none;
    }
  }

  @media (max-width: 720px) {
    right: 16px;
    bottom: 16px;
  }
`;

interface SupportLauncherButtonProps {
  onOpen: () => void;
  /** The Submit Feedback drawer is open — step clear of it, as the panel does. */
  offset?: boolean;
}

const SupportLauncherButton: FC<SupportLauncherButtonProps> = ({ onOpen, offset }) => (
  <Bubble
    type="button"
    $offset={offset}
    // The visible label only appears on hover, so the name says what pressing it DOES and the
    // tooltip says what CoSMo IS.
    aria-label="Open CoSMo, the CSM support assistant"
    title="CoSMo — CSM support"
    data-testid="support-launcher-button"
    onClick={onOpen}
  >
    {/* `alt=""`: the button carries the name. */}
    <img alt="" src={CHAT_SRC} />
    <span className="launcher-label" aria-hidden="true">
      Ask CoSMo
    </span>
  </Bubble>
);

export default SupportLauncherButton;
